import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope, FaUser, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setStatus('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post('/api/contact', formData);
      setStatus('Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      if (err.response) {
        setError(`Error: ${err.response.data.error || 'Something went wrong!'}`);
      } else if (err.request) {
        setError('No response from the server. Please try again later.');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const InputField = ({ icon: Icon, label, type, name, placeholder, rows }) => {
    const isTextarea = type === 'textarea';
    const InputComponent = isTextarea ? 'textarea' : 'input';
    const isFocused = focusedField === name;
    const hasValue = formData[name]?.length > 0;

    return (
      <div className="relative">
        <label
          htmlFor={name}
          className={`absolute left-12 transition-all duration-300 pointer-events-none ${
            isFocused || hasValue
              ? '-top-2 text-xs bg-white px-2 text-primary font-medium'
              : 'top-3 text-gray-500'
          }`}
        >
          {label}
        </label>
        <div className="relative">
          <div className={`absolute left-4 flex items-center h-full transition-colors duration-300 ${
            isFocused ? 'text-primary' : 'text-gray-400'
          }`}>
            <Icon className="text-lg" />
          </div>
          <InputComponent
            type={type}
            id={name}
            name={name}
            placeholder={isFocused ? placeholder : ''}
            value={formData[name]}
            onChange={handleChange}
            onFocus={() => setFocusedField(name)}
            onBlur={() => setFocusedField('')}
            rows={rows}
            required
            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl shadow-sm transition-all duration-300 focus:outline-none ${
              isFocused
                ? 'border-primary shadow-primary/20'
                : 'border-gray-200 hover:border-gray-300'
            } ${isTextarea ? 'resize-none' : ''}`}
          />
        </div>
      </div>
    );
  };

  return (
    <div className='flex justify-center items-center px-4 py-8'>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white shadow-2xl grid grid-cols-1 lg:grid-cols-2 w-full max-w-6xl rounded-2xl overflow-hidden"
      >
        {/* Left Side - Contact Form */}
        <div className="w-full p-8 lg:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-secondary/30">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold">
              <span className="text-primary">Get in </span>
              <span className="gradient-text">Touch</span>
            </h2>
            <p className="text-gray-600 mt-4 text-lg">
              Please fill out the form below, and we'll get back to you as soon as possible.
            </p>
          </motion.div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <InputField
              icon={FaUser}
              label="Full Name *"
              type="text"
              name="name"
              placeholder="Enter your full name"
            />

            <InputField
              icon={FaEnvelope}
              label="Email Address *"
              type="email"
              name="email"
              placeholder="your.email@example.com"
            />

            <InputField
              icon={FaPhoneAlt}
              label="Phone Number *"
              type="tel"
              name="phone"
              placeholder="+250 XXX XXX XXX"
            />

            <InputField
              icon={FaPaperPlane}
              label="Your Message *"
              type="textarea"
              name="message"
              placeholder="Tell us how we can help you..."
              rows="4"
            />

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full bg-gradient-primary text-white py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-primary transition-all duration-300 flex items-center justify-center gap-2 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Send Message
                </>
              )}
            </motion.button>
          </form>

          {/* Status Messages */}
          <AnimatePresence>
            {status && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700"
              >
                <FaCheckCircle className="text-xl flex-shrink-0" />
                <p className="font-medium">{status}</p>
              </motion.div>
            )}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700"
              >
                <FaExclamationCircle className="text-xl flex-shrink-0" />
                <p className="font-medium">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4 p-4 bg-primary/5 rounded-xl">
            {/* Phone Info */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                <FaPhoneAlt className="text-white" size={18} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-500 uppercase">Phone</span>
                <a href="tel:+250784056256" className="text-sm font-semibold text-primary hover:underline">
                  +250 784 056 256
                </a>
              </div>
            </div>

            {/* Email Info */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                <FaEnvelope className="text-white" size={18} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-500 uppercase">Email</span>
                <a href="mailto:kigalianimalfeed@gmail.com" className="text-sm font-semibold text-primary hover:underline">
                  kigalianimalfeed@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Map Section */}
        <div className="w-full h-full min-h-[400px] lg:min-h-full bg-gradient-primary p-4 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative w-full h-full rounded-xl overflow-hidden shadow-2xl z-10">
            <iframe
              title="Kigali Nyabugogo Map"
              src="https://www.google.com/maps?q=-1.937722,30.05075&z=15&output=embed"
              width="100%"
              height="100%"
              allowFullScreen=""
              loading="lazy"
              className="rounded-xl"
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContactForm;
